import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { LangSwitcher } from "../components/LangSwitcher";
import { usePrivateSeo } from "../lib/useSeo";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { ShieldCheck } from "lucide-react";

export default function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  usePrivateSeo(t("auth.signIn"));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await login(email, password);
    setBusy(false);
    if (!res.ok) { setError(res.error); return; }
    toast.success(t("auth.welcome"));
    navigate(res.user.role === "ambassador" ? "/" : "/staff", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="vy-hero px-6 pb-14 pt-6">
        <div className="mx-auto flex max-w-[520px] items-center">
          <span className="font-display text-xl font-bold text-white">
            Vytali<span className="text-accent">Days</span>
          </span>
          <span className="ml-auto"><LangSwitcher dark /></span>
        </div>
        <div className="mx-auto mt-12 max-w-[520px]">
          <h1 className="font-display text-4xl font-extrabold leading-tight text-white sm:text-5xl">
            {t("common.appName")}
          </h1>
          <p className="mt-3 text-base text-white/70">{t("auth.tagline")}</p>
        </div>
      </div>

      <div className="mx-auto -mt-8 max-w-[520px] px-4">
        <form onSubmit={submit} data-testid="login-form" className="vy-card p-6 shadow-sm">
          <h2 className="font-display text-lg font-bold">{t("auth.signIn")}</h2>
          <p className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
            <ShieldCheck className="h-4 w-4" /> {t("auth.inviteOnly")}
          </p>
          <div className="mt-6 space-y-4">
            <div>
              <label className="text-sm font-semibold">{t("auth.email")}</label>
              <Input data-testid="login-email-input" type="email" required value={email}
                autoComplete="email"
                onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-12" />
            </div>
            <div>
              <label className="text-sm font-semibold">{t("auth.password")}</label>
              <Input data-testid="login-password-input" type="password" required value={password}
                autoComplete="current-password"
                onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-12" />
            </div>
          </div>
          {error && (
            <p data-testid="login-error" className="mt-4 rounded-xl bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive">
              {error}
            </p>
          )}
          <Button data-testid="login-submit-btn" type="submit" disabled={busy}
            className="mt-6 h-14 w-full rounded-full text-base font-bold active:scale-95">
            {busy ? t("common.loading") : t("auth.signIn")}
          </Button>
          <Link to="/forgot-password" data-testid="login-forgot-link"
            className="mt-4 block text-center text-sm font-semibold text-muted-foreground hover:text-primary">
            {t("account.forgotTitle")}
          </Link>
        </form>
      </div>
    </div>
  );
}
